import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Modal, Button, Input, TextArea, toast } from '../../components/common';
import { contractApi } from '../../services/api';
import './AmendmentModal.css';

export default function AmendmentModal({ contractId, isOpen, onClose, onSuccess }) {
    const [form, setForm] = useState({
        description: '',
        valueChange: '',
        effectiveDate: ''
    });
    const [errors, setErrors] = useState({});

    const mutation = useMutation({
        mutationFn: (data) => contractApi.createAmendment(contractId, data),
        onSuccess: () => {
            toast.success('Amendment created successfully');
            setForm({ description: '', valueChange: '', effectiveDate: '' });
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to create amendment');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.description) newErrors.description = 'Description is required';
        if (!form.effectiveDate) newErrors.effectiveDate = 'Effective date is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        mutation.mutate({
            description: form.description,
            valueChange: form.valueChange ? parseFloat(form.valueChange) : 0,
            effectiveDate: form.effectiveDate
        });
    };

    const handleClose = () => {
        setForm({ description: '', valueChange: '', effectiveDate: '' });
        setErrors({});
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Create Contract Amendment"
            size="md"
        >
            <form onSubmit={handleSubmit} className="amendment-form">
                <TextArea
                    name="description"
                    label="Amendment Description"
                    placeholder="Describe the changes to the contract..."
                    value={form.description}
                    onChange={handleChange}
                    error={errors.description}
                    rows={3}
                    required
                />

                <div className="form-row">
                    <Input
                        name="valueChange"
                        type="number"
                        label="Value Change"
                        placeholder="0"
                        value={form.valueChange}
                        onChange={handleChange}
                    />
                    <Input
                        name="effectiveDate"
                        type="date"
                        label="Effective Date"
                        value={form.effectiveDate}
                        onChange={handleChange}
                        error={errors.effectiveDate}
                        required
                    />
                </div>

                <div className="form-actions">
                    <Button variant="secondary" type="button" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit" loading={mutation.isPending}>
                        Create Amendment
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

AmendmentModal.propTypes = {
    contractId: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};
